/**
 * Shared auth token resolution with session refresh.
 *
 * Token validity is decided locally from the session's expires_at instead of a
 * network getUser() round-trip per request: that round-trip made every API call
 * only as reliable as two extra auth-server requests, and a transient failure
 * cascaded into a forced sign-out. Refresh is delegated to supabase-js's
 * getSession(), which single-flights concurrent refreshes and rotates the
 * refresh token safely.
 */

import {
  supabase,
  isSupabaseConfigured,
  getCurrentSession,
  setCachedSession,
  redirectToLoginOnSessionExpired,
  isInvalidRefreshTokenError,
} from '@/lib/supabase';

/** Refresh when the access token has less than this many seconds of life left. */
const EXPIRY_MARGIN_S = 60;

/** Definitive loss of session (invalid/revoked refresh token or no session at all).
 * Only this error may trigger a redirect to login. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'SessionExpiredError';
  }
}

function hasUsableToken(session: { access_token?: string; expires_at?: number } | null): boolean {
  if (!session?.access_token) return false;
  if (!session.expires_at) return true;
  return session.expires_at - EXPIRY_MARGIN_S > Math.floor(Date.now() / 1000);
}

export async function resolveAccessToken(): Promise<string> {
  if (!isSupabaseConfigured) {
    throw new Error('Unauthorized');
  }

  const cached = await getCurrentSession();
  if (hasUsableToken(cached)) {
    return cached!.access_token;
  }

  // Expired, near expiry, or missing: getSession() reads storage and refreshes if needed.
  const { data: { session: fresh }, error } = await supabase.auth.getSession();
  if (fresh?.access_token) {
    setCachedSession(fresh);
    return fresh.access_token;
  }

  if (error && isInvalidRefreshTokenError(error)) {
    throw new SessionExpiredError();
  }
  if (!error) {
    // No session and no error: the user is genuinely signed out.
    throw new SessionExpiredError();
  }

  // Transient failure (network/5xx) with a stale token still in hand: use it as a
  // last resort — the API may accept it, and a 401 there is retryable, unlike a
  // forced sign-out here.
  if (cached?.access_token) {
    return cached.access_token;
  }
  throw new Error('Unauthorized');
}

export async function authHeaders(contentType = true): Promise<HeadersInit> {
  const accessToken = await resolveAccessToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };
  if (contentType) headers['Content-Type'] = 'application/json';
  return headers;
}

export async function authHeadersOrRedirect(contentType = true): Promise<HeadersInit> {
  try {
    return await authHeaders(contentType);
  } catch (err) {
    // Only a definitive session loss redirects; transient failures propagate to the
    // caller so the request can fail (and be retried) without logging the user out.
    if (err instanceof SessionExpiredError) {
      await redirectToLoginOnSessionExpired();
    }
    throw err instanceof Error ? err : new Error('Unauthorized');
  }
}
